function formatDisplayDate(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

function formatActivityLine(snapshot) {
  const s = snapshot || {};
  const parts = [
    `${s.callsTotal || 0} calls`,
    `${s.callsConnected || 0} connected`,
  ];
  if ((s.meetingsTotal || 0) > 0) parts.push(`${s.meetingsTotal} meetings`);
  if ((s.walkins || 0) > 0) parts.push(`${s.walkins} walk-ins · met contact`);
  if ((s.siteVisits || 0) > 0) parts.push(`${s.siteVisits} walk-ins · no contact`);
  if ((s.followupsScheduled || 0) > 0) parts.push(`${s.followupsScheduled} follow-ups set`);
  if ((s.companiesAdded || 0) > 0) parts.push(`${s.companiesAdded} companies added`);
  return parts.join(' · ');
}

function getReportTeam(config) {
  const allTeam = (config.salespersons || []).filter(Boolean);
  const selected = config.discordReportUsers;
  if (selected === undefined || selected === null) return [...allTeam].sort((a, b) => a.localeCompare(b));
  if (Array.isArray(selected) && selected.length === 0) return [];
  const selectedSet = new Set(selected.filter(Boolean));
  return allTeam.filter(name => selectedSet.has(name));
}

function getSalespersonDayActivity(date, salesperson, calls, meetings, companies) {
  const dayCalls = calls.filter(c => c.date === date && c.salesperson === salesperson);
  const dayMeetings = meetings.filter(m => m.date === date && m.salesperson === salesperson);
  const dayStart = new Date(date + 'T00:00:00.000Z');
  const dayEnd = new Date(date + 'T23:59:59.999Z');
  const dayCompanies = companies.filter(co => {
    if (co.salesperson !== salesperson) return false;
    const created = new Date(co.createdAt);
    return created >= dayStart && created <= dayEnd;
  });

  const callsTotal = dayCalls.length;
  const callsConnected = dayCalls.filter(c => c.connected).length;
  const followupsScheduled = dayCalls.filter(c => c.followUpDate).length;
  const meetingsTotal = dayMeetings.filter(m => m.type !== 'Walk-in' && m.type !== 'Site visit' && m.type !== 'No-contact visit').length;
  const walkins = dayMeetings.filter(m => m.type === 'Walk-in').length;
  const siteVisits = dayMeetings.filter(m => m.type === 'Site visit' || m.type === 'No-contact visit').length;
  const siteVisitsTotal = walkins + siteVisits;
  const outcomes = {};
  dayCalls.forEach(c => {
    const o = c.conversationOutcome || c.outcome;
    if (o) outcomes[o] = (outcomes[o] || 0) + 1;
  });

  return {
    callsTotal,
    callsConnected,
    callsNotConnected: callsTotal - callsConnected,
    meetingsTotal,
    walkins,
    siteVisits,
    siteVisitsTotal,
    followupsScheduled,
    companiesAdded: dayCompanies.length,
    outcomes,
  };
}

export { getSalespersonDayActivity };

export function buildDailyTeamReport({ date, config, calls, meetings, companies, eodReports = [] }) {
  const team = getReportTeam(config);
  const eodByPerson = Object.fromEntries((eodReports || []).map(r => [r.salesperson, r]));

  const members = team.map(salesperson => {
    const activity = getSalespersonDayActivity(date, salesperson, calls, meetings, companies);
    const eod = eodByPerson[salesperson];
    const note = (eod?.note || '').trim();
    const snapshot = eod?.snapshot || activity;
    const hasActivity = activity.callsTotal > 0 || activity.meetingsTotal > 0 || activity.siteVisitsTotal > 0;

    return {
      salesperson,
      eodSubmitted: !!eod,
      note,
      snapshot: eod ? snapshot : activity,
      hasActivity,
      noteMissing: !note,
    };
  });

  return {
    date,
    teamSize: team.length,
    submitted: members.filter(m => m.eodSubmitted).length,
    missingEod: members.filter(m => !m.eodSubmitted).length,
    missingNotes: members.filter(m => m.eodSubmitted && m.noteMissing).length,
    members,
  };
}

export function buildDiscordReportMessage(report) {
  const lines = [];
  lines.push('📊 **UNADA Daily Report**');
  lines.push(`📅 ${formatDisplayDate(report.date)}`);
  lines.push('');
  lines.push(`Team: **${report.teamSize}** · EOD submitted: **${report.submitted}/${report.teamSize}** · Missing: **${report.missingEod}**`);
  if (report.missingNotes > 0) {
    lines.push(`⚠️ ${report.missingNotes} submitted without notes`);
  }
  lines.push('');

  report.members.forEach((m, i) => {
    const status = m.eodSubmitted
      ? (m.noteMissing ? '⚠️ EOD submitted (no note)' : '✅ EOD submitted')
      : '❌ EOD NOT submitted';
    lines.push(`**${i + 1}. ${m.salesperson}** — ${status}`);
    lines.push(`📞 ${formatActivityLine(m.snapshot)}`);

    if (m.note) {
      lines.push('📝 **Note:**');
      m.note.split('\n').slice(0, 8).forEach(line => {
        const t = line.trim();
        if (t) lines.push(t.startsWith('–') || t.startsWith('-') ? t : `– ${t}`);
      });
      if (m.note.split('\n').length > 8) lines.push('…');
    } else if (!m.eodSubmitted) {
      if (m.hasActivity) lines.push('⚠️ Activity logged but daily note not uploaded');
      else lines.push('⚠️ No activity · No daily note');
    } else {
      lines.push('⚠️ EOD saved without notes');
    }
    lines.push('');
  });

  lines.push('_Unada Sales OS · auto report_');
  return lines.join('\n').trim();
}

function splitMessage(text, maxLen = 1900) {
  if (text.length <= maxLen) return [text];
  const chunks = [];
  let remaining = text;
  while (remaining.length > maxLen) {
    let cut = remaining.lastIndexOf('\n', maxLen);
    if (cut < Math.floor(maxLen * 0.4)) cut = maxLen;
    chunks.push(remaining.slice(0, cut).trim());
    remaining = remaining.slice(cut).trim();
  }
  if (remaining) chunks.push(remaining);
  return chunks;
}

export async function sendDiscordWebhookMessage(webhookUrl, body) {
  const url = (webhookUrl || '').trim();
  if (!url) throw new Error('Add your Discord webhook URL in Settings first.');

  const chunks = splitMessage(body);
  for (let i = 0; i < chunks.length; i++) {
    const chunk = chunks.length > 1 ? `(${i + 1}/${chunks.length})\n${chunks[i]}` : chunks[i];
    const payload = JSON.stringify({ content: chunk });
    // Browser cannot read Discord webhook responses (CORS). Use no-cors + form body.
    const form = new URLSearchParams();
    form.set('payload_json', payload);
    await fetch(url, {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: form.toString(),
    });
    if (i < chunks.length - 1) {
      await new Promise(r => setTimeout(r, 600));
    }
  }
}

export async function sendDailyReportClient({ date, config, calls, meetings, companies, eodReports = [] }) {
  const report = buildDailyTeamReport({ date, config, calls, meetings, companies, eodReports });
  const message = buildDiscordReportMessage(report);
  await sendDiscordWebhookMessage(config.discordWebhookUrl, message);
  return { date, message, report };
}

export function previewDailyReportClient({ date, config, calls, meetings, companies, eodReports = [] }) {
  const report = buildDailyTeamReport({ date, config, calls, meetings, companies, eodReports });
  return buildDiscordReportMessage(report);
}
